import { looksLikeDiscordClientSecret, looksLikeSecret } from '@/Domain/helper/secretShapes';

// Precision over recall: a false positive interrupts someone testing their own Agent. Each rule's
// backtested precision and the families that were measured and rejected are in the PR description.

export type PreviewSupportIntentRule =
  | 'app_surface'
  | 'deploy_you'
  | 'builder_meta'
  | 'dev_surface'
  | 'channel_broken'
  | 'channel_setup'
  | 'brand';

// One bound for both halves: a rule can only fire on text Support AI actually receives, and the
// cap keeps a pasted wall of text out of a Quickchat-tenant conversation.
export const MAX_FORWARDED_MESSAGE_LENGTH = 200;
export const PREVIEW_SUPPORT_INTENT_VERSION = 2;

const collapseWhitespace = (raw: string): string => (raw ?? '').replace(/\s+/g, ' ').trim();

// A pasted embed snippet is setup material, not a question, and it always carries the brand token.
const PASTED_SNIPPET = /<\s*\/?\s*script|_quickchat\s*\(|insertAdjacentElement|chat\.js/i;

const EMAIL = /\S+@\S+\.\w+/g;
const URL = /https?:\/\/\S+/g;

// The widget's own metadata blob, which users paste back into the composer.
const LEADING_JSON = /^\s*\{[^{}]*\}\s*/;
// Japanese and Chinese are unspaced, so a word count cannot judge them. Korean is spaced, so it is out.
const CJK = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uff66-\uff9f]/;
const QUESTION_MARK = /[?\uff1f\u00bf]/;
const MIN_ANSWERABLE_WORDS = 3;

// A bare brand token, a bare app URL or a pasted metadata blob opens a panel with nothing for
// Support AI to answer: 19 of them a year in the backtest, against 4 short continuations lost.
const hasNothingToAnswer = (head: string): boolean => {
  const bare = head.replace(LEADING_JSON, ' ').replace(URL, ' ').replace(EMAIL, ' ');
  if (CJK.test(bare) || QUESTION_MARK.test(bare)) return false;
  return bare.trim().split(' ').filter(Boolean).length < MIN_ANSWERABLE_WORDS;
};

// Names a Quickchat surface or in-app feature label no end customer of theirs would name.
const APP_SURFACE =
  /\bapp\.quickchat\.ai\b|\b(?:where|how|which)\b[^.?!]{0,40}?\b(?:bot token|scenario id|api key|api token|embed code|widget code|external apps?)\b|\b(?:bot token|scenario id|api key|external apps?|knowledge base tab)\b[^.?!]{0,30}?\b(?:page|tab|where|put|get|find|add|input)\b|\b(?:discord|telegram|slack|whats\s?app|shopify)\s+bot\s+integration\b|\bexternal\s+apps?\b|\bai\s+preview\b|\bknowledge\s+base\s+(?:tab|page|section)\b|\bai\s+actions?\b/i;

// "add/deploy YOU to discord/my website"; the second alternation is the same ask reversed, and
// the third is both in Spanish and Portuguese, which an English verb list cannot reach. Its
// destination needs a named channel or an owned surface: a bare preposition would make
// "como te pongo en una canción" a deployment question.
const DEPLOY_YOU =
  /\b(?:add|connect|integrate|install|deploy|invite|put|link|hook|embed|publish|use)\b[^.?!]{0,40}?\b(?:you|u|ur|your|yourself|this|that|it|this ai|this bot|this agent|this chatbot)\b[^.?!]{0,40}?\b(?:discord|slack|telegram|whats\s?app|instagram|messenger|intercom|zendesk|hubspot|shopify|wordpress|wix|squarespace|webflow|my server|our server|my site|my website|our website|our site|my page|my shop|my store)\b|\b(?:add|connect|integrate|install|link|hook|use)\b[^.?!]{0,45}?\b(?:discord|slack|telegram|whats\s?app|instagram|messenger|intercom|zendesk|hubspot|shopify|wordpress|wix)\b[^.?!]{0,35}?\b(?:to|with|on|into)\s+(?:you|u|ur|this|it|here)\b|\b(?:como|cómo)\b[^.?!]{0,20}?\b(?:te|lo|la|o|a)\b[^.?!]{0,10}?\b(?:agrego|añado|anado|adiciono|coloco|conecto|integro|instalo|pongo|meto|ligo)\b[^.?!]{0,30}?\b(?:discord|telegram|whats\s?app|slack|instagram|(?:al|ao|no|na|en|em|el|la|o|a|um|un|una|mi|mis|meu|meus|nuestro|nosso|tu|su)\s+(?:serv\w*|canal|chat|bot|sitio|site|web\w*|p[aá]gina|tienda|loja))\b|\b(?:agregar|añadir|adicionar|colocar|conectar|integrar|instalar)\b[^.?!]{0,25}?\b(?:te|lo|a\s+você|o\s+bot|el\s+bot)\b[^.?!]{0,30}?\b(?:discord|telegram|whats\s?app|slack)\b/i;

// "how do i train/customize you". The second alternation covers a named attribute and accepts a
// declarative lead-in; the first keeps the narrow interrogative set, measured ~94% against ~52%
// widened, because "i want to create ..." is a build request rather than a setup question.
const BUILDER_META =
  /\b(?:how (?:do|can|to)|where (?:do|can)|can i|help me)\b[^.?!]{0,30}?\b(?:train|retrain|create|make|build|configure|set ?up|deploy|publish|customi[sz]e|rename|delete|go live)\b[^.?!]{0,25}?\b(?:you|your (?:name|personality|knowledge|instructions?)|my (?:ai )?(?:agent|bot|chatbot|assistant))\b|\b(?:how (?:do|can|to|would)|where (?:do|can)|can i|help me|i (?:want|need|would like|'?d like) to|i wanna)\b[^.?!]{0,30}?\b(?:change|edit|update|set|replace|upload|give)\b[^.?!]{0,30}?\b(?:your|the bots?|my (?:ai )?(?:agent|bot|chatbot|assistant)(?:'?s)?)\s*(?:a\s+)?(?:name|profile\s+(?:picture|image|photo)|avatar|icon|pfp|personality|behaviou?r|instructions?|greeting|prompt|language|voice)\b/i;

// Names developer-console surfaces that measured as owner setup questions in the audited corpus.
const DEV_SURFACE =
  /\bdeveloper\s+(?:portal|application|app|dashboard|console|account)\b|\bapplication\s+id\b|\bclient\s+secret\b|\boauth2?\s+(?:url|link|tab|scope)\b|\bprivileged\s+intents?\b|\bmessage\s+content\s+intent\b|\bbot\s+permissions?\b|\bdiscord\.com\/developers\b/i;

// "my discord bot is offline" — a deployed agent that stopped working, named with its channel.
const CHANNEL_BROKEN =
  /\b(?:bot|agent|assistant|chatbot|widget|it)\b[^.?!]{0,30}?\b(?:is\s+)?(?:not|isn'?t|doesn'?t|won'?t|can'?t|never)\b[^.?!]{0,25}?\b(?:online|offline|respond(?:ing)?|reply(?:ing)?|answer(?:ing)?|show(?:ing)?\s*up|appear(?:ing)?|work(?:ing)?|connect(?:ing|ed)?)\b[^.?!]{0,40}?\b(?:discord|telegram|slack|whats\s?app|instagram|messenger|my\s+server|our\s+server|my\s+channel)\b|\b(?:discord|telegram|slack|whats\s?app)\b[^.?!]{0,40}?\b(?:bot|agent|assistant)\b[^.?!]{0,25}?\b(?:offline|(?:is\s+)?(?:not|isn'?t|doesn'?t|won'?t|can'?t|never)\b[^.?!]{0,25}?\b(?:online|offline|respond(?:ing)?|reply(?:ing)?|answer(?:ing)?|show(?:ing)?\s*up|appear(?:ing)?|work(?:ing)?|connect(?:ing|ed)?))\b/i;

// The broad channel net: a question word plus a provider plus one of its surfaces. The last two
// alternations are the same ask stated as a request, which is how about half of them arrive.
const CHANNEL_SETUP =
  /\b(?:how|where|why|what)\b[^.?!]{0,50}?\b(?:discord|telegram|slack|whats\s?app)\b[^.?!]{0,40}?\b(?:server|guild|channel|workspace|bot|integration|app)\b|\b(?:discord|telegram|slack|whats\s?app)\b[^.?!]{0,40}?\b(?:server|guild|channel|workspace)\b[^.?!]{0,40}?\b(?:how|where|why|set\s?up|setup|add|connect)\b|\b(?:how (?:do|can|to|would)|where (?:do|can)|can i|help me|i (?:want|need|would like|'?d like) to|i wanna)\b[^.?!]{0,40}?\b(?:add|connect|integrate|install|deploy|invite|put|link|hook|embed|publish|use)\b[^.?!]{0,40}?\b(?:discord|telegram|slack|whats\s?app|instagram|messenger)\b[^.?!]{0,40}?\b(?:server|guild|channel|workspace|bot|integration|app)\b|\b(?:how (?:do|can|to|would)|where (?:do|can)|can i|help me|i (?:want|need|would like|'?d like) to|i wanna)\b[^.?!]{0,40}?\b(?:add|connect|integrate|install|deploy|invite|put|link|hook|embed|publish|use)\b[^.?!]{0,30}?\b(?:my|the|a)\s+(?:ai\s+)?(?:bot|agent|assistant|chatbot)\b[^.?!]{0,30}?\b(?:discord|telegram|slack|whats\s?app|instagram|messenger)\b/i;

// The whole of CHANNEL_SETUP's measured false-positive set: a third-party dev tool they mean to
// build with instead, or a roleplay question whose subject is the preview agent itself.
const CHANNEL_SETUP_VETO =
  /\b(?:discord\.?\s?js|discordia|discord\.?\s?py|pycord|nextcord|bot\s+designer|replit|repl\.it|node\.?\s?js|python|javascript|java|c\+\+|hytale|self.?host|host(?:ing)?\s+(?:app|server|site|provider)|vps)\b|\b(?:how\s+do\s+you\s+feel|what(?:'|’)?s?\s+(?:is\s+)?your\s+name|what\s+are\s+you\b|do\s+you\s+think\s+you|can\s+you\s+give\s+a\s+reason|do\s+you\s+like|would\s+you\s+like|are\s+you\s+able\s+to\s+feel)\b/i;

// Broadest and noisiest, so it runs last.
const BRAND = /\bquick\s?chat(?:\s*\.?\s*ai)?\b/i;

// Two families that carry the brand token without asking Quickchat anything: asking the Agent to
// write copy about us, and chatting to it about having been built with us. Second person on
// purpose — generalising to "X by quickchat" would kill the real question "Can Powered by
// quickchat.ai be removed?".
const BRAND_VETO =
  /\b(?:write|compose|draft|generate|rewrite)\b[^.?!]{0,40}?\b(?:message|post|article|essay|text|words|copy|blog|poem|song|story|tweet|caption|paragraph|description)\b[^.?!]{0,40}?\b(?:about|sobre|acerca)\b[^.?!]{0,20}?\bquick\s?chat|\b(?:(?:i|we)\s+(?:created|made|built|programmed|designed)\s+(?:you|u)|created\s+you|you\s+(?:were|was|are)\s+(?:created|made|built)|thanks?\s+(?:you\s+)?,?\s*quick\s?chat)\b/i;

const OWNER_APPEARANCE_EN =
  /\b(?:how (?:do|can|to)|where (?:do|can)|can (?:i|you)|i (?:want|need) to)\b[^.?!]{0,30}?\b(?:change|edit|update|set|replace|upload)\b[^.?!]{0,20}?\b(?:your|(?:my|the) (?:ai )?(?:bot|agent|chatbot)(?:'s)?)\s+(?:profile\s+(?:picture|pic|image|photo)|avatar|icon|pfp)\b|\b(?:how (?:do|can|to)|where (?:do|can)|can (?:i|you)|i (?:want|need) to)\b[^.?!]{0,30}?\b(?:change|edit|update|set|replace|upload)\b[^.?!]{0,15}?\b(?:profile\s+(?:picture|pic|image|photo)|avatar|icon|pfp)\s+(?:of|for)\s+(?:(?:my|the)\s+)?(?:ai\s+)?(?:bot|agent|chatbot)\b|\bwhere (?:do|can) i\b[^.?!]{0,20}?\bsettings for my (?:ai )?(?:bot|agent|chatbot)\b[^.?!]{0,40}?\b(?:set|change|upload)\s+(?:an?\s+)?(?:avatar|pfp|profile picture)\b/i;

const OWNER_APPEARANCE_CHANNEL_EN =
  /\bdiscord\b[^.?!]{0,40}\bwhy\s+didn'?t\s+you\s+(?:change|update|set)\s+(?:your\s+)?(?:pfp|avatar|profile picture)\b/i;

const OWNER_APPEARANCE_ES =
  /\b(?:como|cómo|puedo|puedes|quiero|necesito|donde|dónde)\b[^.?!]{0,35}?\b(?:cambiar|editar|modificar|poner)\s+tu\s+(?:foto(?: de perfil)?|avatar|imagen de perfil)\b|\b(?:no encuentro|donde|dónde)\b[^.?!]{0,25}?\bcambiarte\s+(?:la\s+)?(?:foto|avatar)\b/iu;

const OWNER_APPEARANCE_VI =
  /(?:đổi|doi)\s+(?:(?:được|duoc)\s+)?(?:av(?:atar|artar|arta|t)|ảnh đại diện|anh dai dien)\s+(?:của bạn|cua ban)/iu;

const OWNER_APPEARANCE_AR =
  /(?:كيف|شلون|ازاي)[^.?!؟]{0,25}(?:[اأ]غير|تغيير)\s+(?:صور[ةه]|افتار|[اأ]فاتار)\s+(?:البوت|بوتي)/u;

const OWNER_APPEARANCE_KO = /(?:^|\s)(?:너의|네)\s*(?:프로필|아바타)[^.?!]{0,15}(?:바꾸|변경)/u;

const OWNER_DEPLOYMENT_PT =
  /\b(?:como|consigo|posso|para|pra)\b[^.?!]{0,20}?\bte\s+(?:conectar|adicionar|colocar|integrar)\b[^.?!]{0,35}?\b(?:discord|telegram|whats\s?app|slack|(?:meu|nosso|no|ao)\s+(?:server|servidor|site))\b/iu;

const OWNER_DEPLOYMENT_VI =
  /(?:làm sao|lam sao|làm thế nào|lam the nao)[^.?!]{0,25}(?:thêm|them|add|kết nối|ket noi)\s+(?:bạn|ban)[^.?!]{0,25}(?:discord|telegram|whats\s?app|slack)/iu;

const OWNER_DEPLOYMENT_AR =
  /(?:كيف|شلون|ازاي|(?:عطيني|[اأ]عطني)\s+رابط)[^.?!؟]{0,25}(?:[اأ]ضيفك|[اأ]دخلك)[^.?!؟]{0,30}(?:سيرفر|السيرفر)|(?:سيرفر|السيرفر)[^.?!؟]{0,25}(?:كيف|شلون|ازاي)[^.?!؟]{0,15}(?:[اأ]ضيفك|[اأ]دخلك)/u;

const OWNER_DELETE_PT =
  /\bcomo\s+(?:(?:eu\s+)?(?:posso|consigo|devo)\s+)?(?:excluir|apagar|deletar|remover)\s+(?:o\s+)?meu\s+(?:agente|bot|chatbot)\b/iu;

// Sharpest first, so the reported rule is deterministic and the noisiest one reports only what the
// others missed. The channel rules sit above brand so the specific reason wins over the bare token.
const RULES: ReadonlyArray<[PreviewSupportIntentRule, RegExp]> = [
  ['app_surface', APP_SURFACE],
  ['deploy_you', DEPLOY_YOU],
  ['builder_meta', BUILDER_META],
  ['dev_surface', DEV_SURFACE],
  ['channel_broken', CHANNEL_BROKEN],
  ['channel_setup', CHANNEL_SETUP],
  ['builder_meta', OWNER_APPEARANCE_EN],
  ['builder_meta', OWNER_APPEARANCE_CHANNEL_EN],
  ['builder_meta', OWNER_APPEARANCE_ES],
  ['builder_meta', OWNER_APPEARANCE_VI],
  ['builder_meta', OWNER_APPEARANCE_AR],
  ['builder_meta', OWNER_APPEARANCE_KO],
  ['builder_meta', OWNER_DELETE_PT],
  ['deploy_you', OWNER_DEPLOYMENT_PT],
  ['deploy_you', OWNER_DEPLOYMENT_VI],
  ['deploy_you', OWNER_DEPLOYMENT_AR],
  ['brand', BRAND],
];

// Each recognizes an identifying URL as signal in its own right, so they read the unstripped text.
const READS_UNSTRIPPED: ReadonlySet<PreviewSupportIntentRule> = new Set([
  'app_surface',
  'dev_surface',
]);

// Vetoed rather than narrowed into the pattern: these families are about what the message is for,
// not about its shape, so a veto keeps them out of every later widening of the rule too.
const VETOES: Partial<Record<PreviewSupportIntentRule, RegExp>> = {
  channel_setup: CHANNEL_SETUP_VETO,
  brand: BRAND_VETO,
};

export const detectPreviewSupportIntent = (raw: string): PreviewSupportIntentRule | null => {
  const head = collapseWhitespace(raw).slice(0, MAX_FORWARDED_MESSAGE_LENGTH);
  if (!head || PASTED_SNIPPET.test(head)) return null;
  if (hasNothingToAnswer(head)) return null;

  // Emails were the largest false-positive family; app URLs are their own signal, so app_surface
  // reads the unstripped head.
  const stripped = head.replace(EMAIL, ' ').replace(URL, ' ');

  for (const [rule, pattern] of RULES) {
    const subject = READS_UNSTRIPPED.has(rule) ? head : stripped;
    if (!pattern.test(subject)) continue;
    if (VETOES[rule]?.test(subject)) continue;
    return rule;
  }
  return null;
};

export const REDACTED = '[redacted]';

// Whole-word, so a pattern that matches only a prefix (a URI scheme, a Bearer keyword) cannot leave
// the rest of the credential behind.
const redactCredentials = (collapsed: string): string => {
  const words = collapsed.split(' ');
  return words
    .map((word, index) => {
      const containsDiscordClientSecret = word
        .split(/[^A-Za-z0-9_-]+/)
        .some(looksLikeDiscordClientSecret);
      if (looksLikeSecret(word) || containsDiscordClientSecret) return REDACTED;
      // Only for a pattern that spans the space, such as `Bearer <token>`: a previous word that
      // matches on its own is already redacted and must not take this one with it.
      const previous = words[index - 1];
      const spansTheSpace =
        previous !== undefined &&
        !looksLikeSecret(previous) &&
        looksLikeSecret(`${previous} ${word}`);
      return spansTheSpace ? REDACTED : word;
    })
    .join(' ');
};

export const forwardableMessage = (raw: string): string => {
  // Redact before the cap, so a token straddling it cannot be forwarded in part.
  const collapsed = redactCredentials(collapseWhitespace(raw));
  return collapsed.length > MAX_FORWARDED_MESSAGE_LENGTH
    ? `${collapsed.slice(0, MAX_FORWARDED_MESSAGE_LENGTH).trimEnd()}...`
    : collapsed;
};
